import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type AccessGrant,
  RoleGrantsField,
  roleAccessBody,
  TeamGrantsField,
  teamAccessBody,
} from '@/entities/collab';
import {
  DOCUMENT_NAME_MAX,
  useCreateDocument,
  useImportDocument,
  type WorkspaceDocument,
} from '@/entities/document';
import type { Workspace } from '@/entities/workspace';
import { FromTemplateButton } from '@/features/template-manage';
import { FormError } from '@/shared/forms';
import { Button, Dialog, DialogActions, Icons, TextField } from '@/shared/ui';

import styles from './CreateDocument.module.css';
import { useDocumentPlace } from './DocumentPlace';

interface Common {
  /** Where a note may be created (the viewer has the right there). */
  workspaces: readonly Workspace[];
  /** Preselected workspace; with a section or group it is fixed (the page already knows the place). */
  workspaceId?: string | undefined;
  sectionId?: string | null | undefined;
  groupId?: string | null | undefined;
  onCreated: (document: WorkspaceDocument) => void;
  onClose: () => void;
}

/**
 * "New note": the title, where it goes, and who can open it from the start. Access is the same
 * team / role grants as boards and databases; with none the note stays private.
 */
export function CreateDocumentDialog({ onCreated, onClose, ...place }: Common) {
  const { t } = useTranslation();
  const where = useDocumentPlace(place);
  const create = useCreateDocument(where.workspace?.id ?? '');
  const [name, setName] = useState('');
  const [teams, setTeams] = useState<AccessGrant[]>([]);
  const [roles, setRoles] = useState<AccessGrant[]>([]);
  const workspace = where.workspace;
  const isTeam = workspace?.workspace_type === 'team';
  const shared = teams.length + roles.length > 0;
  const formId = 'document-create-form';
  return (
    <Dialog
      title={t('documents.createTitle')}
      onClose={onClose}
      footer={
        <DialogActions>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            disabled={create.isPending || !workspace}
          >
            {t('documents.create')}
          </Button>
        </DialogActions>
      }
    >
      <FromTemplateButton<WorkspaceDocument>
        kind="document"
        workspaceId={where.workspace?.id}
        sectionId={where.body.section_id}
        groupId={where.body.group_id}
        onCreated={onCreated}
      />
      <form
        id={formId}
        className={styles.form}
        data-testid="document-create-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (!workspace) return;
          create.mutate(
            {
              name: name.trim() || t('documents.untitled'),
              ...where.body,
              ...(isTeam && teams.length > 0 && { team_access: teamAccessBody(teams) }),
              ...(isTeam && roles.length > 0 && { role_access: roleAccessBody(roles) }),
            },
            { onSuccess: onCreated },
          );
        }}
      >
        <div className={styles.fields}>
          <TextField
            label={t('documents.name')}
            value={name}
            maxLength={DOCUMENT_NAME_MAX}
            autoFocus
            placeholder={t('documents.untitled')}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
          {where.picker}
        </div>
        <fieldset className={styles.access}>
          <div className={styles.accessHead}>
            <Icons.lock />
            <div>
              <legend className={styles.accessTitle}>{t('documents.createAccess.title')}</legend>
              <p className={styles.accessSummary} data-testid="document-access-summary">
                {t(
                  !isTeam
                    ? 'documents.createAccess.summaryPersonal'
                    : shared
                      ? 'documents.createAccess.summaryShared'
                      : 'documents.createAccess.summaryPrivate',
                )}
              </p>
            </div>
          </div>
          {workspace?.workspace_type === 'team' && (
            <>
              <TeamGrantsField
                workspaceId={workspace.id}
                enabled
                value={teams}
                onChange={setTeams}
                texts={{
                  title: t('documents.createAccess.teamsTitle'),
                  hint: t('documents.createAccess.teamsHint'),
                  add: t('databases.createAccess.addTeam'),
                  remove: (who) => t('databases.createAccess.removeGrant', { name: who }),
                }}
              />
              <hr className={styles.divider} />
              <RoleGrantsField
                workspaceId={workspace.id}
                enabled
                value={roles}
                onChange={setRoles}
                texts={{
                  title: t('documents.createAccess.rolesTitle'),
                  hint: t('documents.createAccess.rolesHint'),
                  add: t('databases.createAccess.addRole'),
                  remove: (who) => t('databases.createAccess.removeGrant', { name: who }),
                }}
              />
            </>
          )}
        </fieldset>
        <FormError error={create.error} />
      </form>
    </Dialog>
  );
}

/** A new note from a Markdown file, private to its creator until shared. */
export function ImportDocumentDialog({ onCreated, onClose, ...place }: Common) {
  const { t } = useTranslation();
  const where = useDocumentPlace(place);
  const importing = useImportDocument(where.workspace?.id ?? '');
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const formId = 'document-import-form';
  return (
    <Dialog
      title={t('documents.importTitle')}
      onClose={onClose}
      footer={
        <DialogActions>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            disabled={!file || importing.isPending || !where.workspace}
          >
            {t('documents.importAction')}
          </Button>
        </DialogActions>
      }
    >
      <form
        id={formId}
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          if (!file) return;
          importing.mutate(
            {
              file,
              name: file.name.replace(/\.(md|markdown|txt)$/i, '').slice(0, DOCUMENT_NAME_MAX),
              section_id: where.body.section_id,
              group_id: where.body.group_id,
            },
            { onSuccess: onCreated },
          );
        }}
      >
        <p className={styles.accessSummary}>{t('documents.importHint')}</p>
        {where.picker}
        <input
          ref={input}
          type="file"
          accept=".md,.markdown,.txt,text/markdown,text/plain"
          aria-label={t('documents.importFile')}
          data-testid="document-import-file"
          onChange={(event) => {
            setFile(event.target.files?.[0] ?? null);
          }}
        />
        <FormError error={importing.error} />
      </form>
    </Dialog>
  );
}
